/**
 * @file api-content-media.ts
 * @description Клиент `/admin/content/media` (phase12.0). Загрузка картинок
 * для статей блога. Использует FormData (multipart). Кабинет вкладки
 * (phase19.31, SC21) — заголовок `tabContextHeaders` в каждом запросе.
 * phase19.16 Р1 — фильтр списка для «Выбрать из медиатеки» и адрес по id.
 */

import { tabContextHeaders } from "./tab-context";

const API_BASE_URL = import.meta.env.VITE_API_URL ?? "/api/v1";

export interface ContentMediaAsset {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  width: number | null;
  height: number | null;
  altText: string | null;
  url: string;
  createdAt: string;
}

/** Загрузка через FormData — apiFetch (json-only) не подходит. */
export async function uploadContentMedia(
  file: File,
  altText?: string,
): Promise<ContentMediaAsset> {
  const form = new FormData();
  form.append("file", file);
  if (altText) form.append("altText", altText);
  const res = await fetch(`${API_BASE_URL}/admin/content/media`, {
    method: "POST",
    credentials: "include",
    headers: tabContextHeaders(`/admin/content/media`),
    body: form,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const message =
      (body as { error?: { message?: string } }).error?.message ??
      `Upload failed (${res.status})`;
    throw new Error(message);
  }
  const body = (await res.json()) as { data: ContentMediaAsset };
  return body.data;
}

/**
 * Фильтр списка медиатеки (phase19.16 Р1, контракт site-view §10): `image` —
 * только изображения, `q` — поиск по имени файла и alt. Без параметров —
 * прежний ответ.
 */
export interface ContentMediaQuery {
  type?: "image";
  q?: string;
}

export async function listContentMedia(query: ContentMediaQuery = {}): Promise<ContentMediaAsset[]> {
  const params = new URLSearchParams();
  if (query.type) params.set("type", query.type);
  if (query.q?.trim()) params.set("q", query.q.trim());
  const qs = params.toString();
  const path = `/admin/content/media${qs ? `?${qs}` : ""}`;
  const res = await fetch(`${API_BASE_URL}${path}`, {
    credentials: "include",
    headers: tabContextHeaders(path),
  });
  if (!res.ok) throw new Error(`Не удалось загрузить медиатеку (${res.status})`);
  const body = (await res.json()) as { data: ContentMediaAsset[] };
  return body.data;
}

/**
 * Стабильный адрес картинки медиатеки по id (`/api/v1/public/content/
 * media/<id>`, v0.19.43) — для мест, где сервер отдаёт только id (фото
 * автора блога).
 */
export function contentMediaUrl(id: string): string {
  return `${API_BASE_URL}/public/content/media/${encodeURIComponent(id)}`;
}

export async function deleteContentMedia(id: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/admin/content/media/${id}`, {
    method: "DELETE",
    credentials: "include",
    headers: tabContextHeaders(`/admin/content/media/${id}`),
  });
  if (!res.ok && res.status !== 204) {
    throw new Error(`Delete failed (${res.status})`);
  }
}
